import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, LOGO_MARK } from "../lib/site";
import { getLenis } from "../lib/smooth";
import { SearchNav } from "./SearchNav";

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const lenis = getLenis();
        if (open) {
            lenis?.stop();
            document.body.style.overflow = "hidden";
        } else {
            lenis?.start();
            document.body.style.overflow = "";
        }
    }, [open]);

    return (
        <header
            data-testid="navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
                scrolled
                    ? "border-b border-white/5 bg-[#04150D] shadow-[0_10px_40px_rgba(0,0,0,0.35)]"
                    : "bg-gradient-to-b from-[#04150D]/85 to-transparent"
            }`}
        >
            <nav
                className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:px-12"
                aria-label="Main navigation"
            >
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="flex items-center gap-3"
                    aria-label="Global Green Hotel — home"
                >
                    <img
                        src={LOGO_MARK}
                        alt="Global Green Hotel logo"
                        className="h-11 w-auto drop-shadow-[0_0_18px_rgba(194,161,96,0.25)]"
                    />
                    <span className="hidden sm:block">
                        <span className="font-display block text-base font-semibold tracking-wide text-[#FDFBF7]">
                            Global Green Hotel
                        </span>
                        <span className="block text-[10px] font-bold uppercase tracking-[0.3em] text-[#C2A160]">
                            Sustainable Hospitality
                        </span>
                    </span>
                </Link>

                <ul className="hidden items-center gap-6 xl:flex">
                    {NAV_LINKS.map((link) => (
                        <li key={link.to}>
                            <NavLink
                                to={link.to}
                                data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                className={({ isActive }) =>
                                    `text-[13px] font-bold tracking-wide transition-colors duration-200 hover:text-[#D9B96A] ${
                                        isActive ? "text-[#C2A160]" : "text-[#FDFBF7]/80"
                                    }`
                                }
                            >
                                {link.label}
                            </NavLink>
                        </li>
                    ))}
                </ul>

                <div className="flex items-center gap-3">
                    <SearchNav />
                    <Link
                        to="/login"
                        data-testid="nav-cta-login"
                        className="hidden rounded-full border border-[#FDFBF7]/25 px-6 py-2.5 text-sm font-bold tracking-wide text-[#FDFBF7] transition-[border-color,background-color] duration-300 hover:border-[#C2A160] hover:bg-[#C2A160]/10 md:inline-flex"
                    >
                        Login
                    </Link>
                    <Link
                        to="/partners"
                        data-testid="nav-cta-partner"
                        className="btn-gold hidden !px-6 !py-2.5 md:inline-flex"
                    >
                        Partner With GGH
                        <ArrowUpRight size={16} aria-hidden="true" />
                    </Link>
                    <button
                        type="button"
                        data-testid="mobile-menu-button"
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        onClick={() => setOpen((v) => !v)}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#FDFBF7]/25 text-[#FDFBF7] transition-colors hover:border-[#C2A160] hover:text-[#C2A160] xl:hidden"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="grain fixed inset-0 top-20 z-40 flex flex-col overflow-y-auto bg-[#04150D] px-8 pb-10 pt-6 xl:hidden"
                    >
                        <ul className="flex flex-col divide-y divide-white/5">
                            {NAV_LINKS.map((link, i) => (
                                <motion.li
                                    key={link.to}
                                    initial={{ opacity: 0, x: -18 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.06 * i, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <NavLink
                                        to={link.to}
                                        data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                        className={({ isActive }) =>
                                            `font-display block py-4 text-2xl tracking-tight ${
                                                isActive ? "text-[#C2A160]" : "text-[#FDFBF7]"
                                            }`
                                        }
                                    >
                                        {link.label}
                                    </NavLink>
                                </motion.li>
                            ))}
                        </ul>
                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.45 }}
                            className="mt-8"
                        >
                            <Link to="/partners" data-testid="mobile-nav-cta-partner" className="btn-gold w-full">
                                Partner With GGH
                                <ArrowUpRight size={16} aria-hidden="true" />
                            </Link>
                            <Link to="/login" data-testid="mobile-nav-cta-login" className="btn-outline-light mt-3 w-full">
                                Login
                            </Link>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};
